export interface CaseEvent {
  date: string;
  title: string;
  description: string;
}

export interface CaseQuote {
  date: string;
  text: string;
  source: string;
}

export interface GuaCase {
  id: string;
  slug: string;
  name: string;
  handle: string;
  category: string;
  platform: string;
  heat: number;
  fallDate: string;
  avatar: string;
  cover: string;
  summary: string;
  tags: string[];
  focus: string[];
  timeline: CaseEvent[];
  quotes: CaseQuote[];
  outcome: string;
}

export const caseCategories = [
  {
    key: "虚假人设",
    name: "虚假人设",
    icon: "ri-user-star-line",
    desc: "学历、经历、身份与他人设对不上，细扒后处处是破绽。",
  },
  {
    key: "言行打脸",
    name: "言行打脸",
    icon: "ri-chat-quote-line",
    desc: "公开说过的漂亮话，与私下言论和实际做法互相矛盾。",
  },
  {
    key: "抄袭搬运",
    name: "抄袭搬运",
    icon: "ri-file-copy-2-line",
    desc: "文案、分镜与创意来源被逐帧对比，原作者现身喊话。",
  },
  {
    key: "带货翻车",
    name: "带货翻车",
    icon: "ri-shopping-bag-3-line",
    desc: "口播推荐与实际体验差距过大，售后问题集中爆发。",
  },
  {
    key: "数据注水",
    name: "数据注水",
    icon: "ri-line-chart-line",
    desc: "播放、粉丝与互动数据异常，第三方监测曲线过于光滑。",
  },
];

export const cases: GuaCase[] = [
  {
    id: "c1",
    slug: "dora-burenlu-xueren",
    name: "朵拉不认路",
    handle: "@朵拉不认路",
    category: "虚假人设",
    platform: "短视频平台",
    heat: 98,
    fallDate: "2026-07-12",
    avatar:
      "https://readdy.ai/api/search-image?query=Stylized%20flat%20editorial%20portrait%20illustration%20of%20a%20young%20female%20online%20creator%20with%20shoulder%20length%20hair%20and%20round%20glasses%2C%20soft%20cream%20background%2C%20warm%20terracotta%20accent%20tones%2C%20minimal%20geometric%20shapes%2C%20centered%20square%20composition%2C%20clean%20no%20text&width=400&height=400&seq=gua-avatar-c1&orientation=squarish",
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20magazine%20illustration%20about%20a%20fake%20academic%20persona%20falling%20apart%2C%20abstract%20stylized%20scene%20with%20torn%20paper%20diploma%20and%20scattered%20documents%20under%20a%20spotlight%2C%20warm%20cream%20and%20terracotta%20palette%2C%20moody%20dramatic%20lighting%2C%20no%20text%20or%20letters&width=800&height=600&seq=gua-cover-c1&orientation=landscape",
    summary:
      "以「名校学霸」「多年科研从业者」标签走红的朵拉，被网友翻出早年经历与口述自相矛盾。从院校名称到研究方向，多处对不上号，一条「学历存疑」的对比帖把整条时间线串了起来。",
    tags: ["学霸人设", "学历存疑", "旧料对比", "口述矛盾"],
    focus: [
      "自述院校与公开可查信息不一致",
      "不同直播中研究方向说法反复变动",
      "早年采访与近期简介中的工作年限对不上",
    ],
    timeline: [
      {
        date: "2024-11-03",
        title: "一句「科班出身」埋下伏笔",
        description:
          "在一场涨粉答疑直播中，朵拉提到自己「科班出身、做过好几年实验室」，被粉丝剪成高光片段，成为其学霸人设的起点。",
      },
      {
        date: "2025-05-18",
        title: "简介悄悄改版",
        description:
          "主页简介从「某高校在读」调整为更模糊的「科研从业者」，当时并未引发注意，但成了后来网友对比的关键节点。",
      },
      {
        date: "2026-06-30",
        title: "旧料对比帖出现",
        description:
          "有网友整理了三年内她在直播、采访、图文三种场合对同一段经历的描述，做成对比图，指出至少三处对不上号。",
      },
      {
        date: "2026-07-12",
        title: "塌房高峰",
        description:
          "多个对比版本在讨论区传播，相关话题登上热榜，评论区从「支持」转为「求一个完整说明」。",
      },
    ],
    quotes: [
      {
        date: "2024-11-03",
        text: "我算是科班出身，实验室待了好几年，所以讲这些会比较细。",
        source: "直播回放片段",
      },
      {
        date: "2025-08-21",
        text: "读书那会儿我主要做的是偏应用的方向，跟现在讲的其实不太一样。",
        source: "粉丝群语音整理",
      },
      {
        date: "2026-03-09",
        text: "我从来没说过具体是哪所学校啊，大家不要自己脑补。",
        source: "评论区回复截图",
      },
    ],
    outcome: "账号更新频率明显下降，主页简介清空，未发布正式说明，相关话题热度在两周后回落。",
  },
  {
    id: "c2",
    slug: "laozhou-gongyi-fanpai",
    name: "老周的日常",
    handle: "@老周的日常",
    category: "言行打脸",
    platform: "图文社区",
    heat: 92,
    fallDate: "2026-05-27",
    avatar:
      "https://readdy.ai/api/search-image?query=Stylized%20flat%20editorial%20portrait%20illustration%20of%20a%20middle%20aged%20male%20online%20creator%20with%20short%20hair%20and%20light%20beard%2C%20soft%20cream%20background%2C%20muted%20teal%20accent%20tones%2C%20minimal%20geometric%20shapes%2C%20centered%20square%20composition%2C%20clean%20no%20text&width=400&height=400&seq=gua-avatar-c2&orientation=squarish",
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20magazine%20illustration%20about%20contradictory%20public%20statements%2C%20abstract%20stylized%20scene%20with%20two%20overlapping%20speech%20bubbles%20and%20a%20cracked%20mirror%2C%20warm%20cream%20and%20teal%20palette%2C%20moody%20dramatic%20lighting%2C%20no%20text%20or%20letters&width=800&height=600&seq=gua-cover-c2&orientation=landscape",
    summary:
      "长期以「公益志愿者」形象分享乡村见闻的老周，被翻出早年小号里与公益人设明显冲突的言论。前后态度的对比让不少老粉直呼「滤镜碎了」。",
    tags: ["公益人设", "小号发言", "前后矛盾", "老粉脱粉"],
    focus: [
      "公开倡导与早年小号言论方向相反",
      "合作活动性质描述前后不一致",
      "对同一件事在不同平台给出两套说法",
    ],
    timeline: [
      {
        date: "2023-09-14",
        title: "公益内容起量",
        description:
          "一组乡村走访图文获得大量转发，老周开始以「长期做公益的人」身份被更多人认识。",
      },
      {
        date: "2025-04-02",
        title: "被质疑行程缩水",
        description:
          "有读者指出文中提到的多个走访点其实是同一地点拍摄，行程存在缩水，当时只在小范围讨论。",
      },
      {
        date: "2026-05-19",
        title: "早年小号被翻出",
        description:
          "网友整理出其五年前小号中与公益立场相反的发言，时间线与如今内容形成鲜明对比。",
      },
      {
        date: "2026-05-27",
        title: "话题集中爆发",
        description:
          "对比长帖扩散，多个老粉发文告别，「人设」成为评论区高频词。",
      },
    ],
    quotes: [
      {
        date: "2023-09-20",
        text: "做这些不是为了流量，就是觉得该有人把真实的一面记下来。",
        source: "图文置顶评论",
      },
      {
        date: "2026-05-19",
        text: "（旧帖整理）这类事情本来就没什么意义，纯粹是做给外人看的。",
        source: "早年小号发言",
      },
    ],
    outcome: "账号停止更新公益系列，团队未作公开回应，相关合作内容陆续下架。",
  },
  {
    id: "c3",
    slug: "tianxinxiaolu-chaoxi",
    name: "甜心小鹿",
    handle: "@甜心小鹿",
    category: "抄袭搬运",
    platform: "短视频平台",
    heat: 89,
    fallDate: "2026-08-03",
    avatar:
      "https://readdy.ai/api/search-image?query=Stylized%20flat%20editorial%20portrait%20illustration%20of%20a%20young%20female%20content%20creator%20with%20long%20wavy%20hair%20and%20a%20hairpin%2C%20soft%20cream%20background%2C%20warm%20rose%20accent%20tones%2C%20minimal%20geometric%20shapes%2C%20centered%20square%20composition%2C%20clean%20no%20text&width=400&height=400&seq=gua-avatar-c3&orientation=squarish",
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20magazine%20illustration%20about%20copying%20creative%20work%2C%20abstract%20stylized%20scene%20with%20overlapping%20paper%20layers%20and%20a%20magnifying%20glass%2C%20warm%20cream%20and%20rose%20palette%2C%20moody%20dramatic%20lighting%2C%20no%20text%20or%20letters&width=800&height=600&seq=gua-cover-c3&orientation=landscape",
    summary:
      "以治愈系文案走红的甜心小鹿，被多位写作者指出其高赞图文与更早发布的作品高度相似。逐句对比图流传后，原作者集中发声。",
    tags: ["文案洗稿", "逐句对比", "原作者发声", "高赞搬运"],
    focus: [
      "多篇高赞文案与更早作品句式高度接近",
      "配图构图与排版顺序几乎一致",
      "被指合理引用之外的整段搬运",
    ],
    timeline: [
      {
        date: "2025-12-11",
        title: "治愈系文案走红",
        description:
          "一组关于生活松弛感的短文案获得大量收藏，账号粉丝在一个月内翻倍。",
      },
      {
        date: "2026-06-22",
        title: "最早对比出现",
        description:
          "一位写作者贴出自己两年前的同题作品，指出段落结构几乎相同，当时讨论范围有限。",
      },
      {
        date: "2026-07-28",
        title: "多位作者联合整理",
        description:
          "陆续有多位作者加入，整理出十余处相似内容，并附上各自的发布时间截图。",
      },
      {
        date: "2026-08-03",
        title: "塌房高峰",
        description:
          "对比合集被广泛转发，账号评论区出现大量「标注来源」呼声。",
      },
    ],
    quotes: [
      {
        date: "2026-02-16",
        text: "这些文案都是我自己写的生活感受，希望大家喜欢。",
        source: "视频结尾口播",
      },
      {
        date: "2026-08-04",
        text: "相似的地方我也很意外，可能大家都在观察同一种生活吧。",
        source: "评论区回应",
      },
    ],
    outcome: "部分争议内容被删除，账号在简介中加入「部分素材整理自网络，侵删」说明。",
  },
  {
    id: "c4",
    slug: "akai-bujiaban-daihuo",
    name: "阿凯不加班",
    handle: "@阿凯不加班",
    category: "带货翻车",
    platform: "直播平台",
    heat: 95,
    fallDate: "2026-09-01",
    avatar:
      "https://readdy.ai/api/search-image?query=Stylized%20flat%20editorial%20portrait%20illustration%20of%20a%20confident%20young%20male%20online%20host%20wearing%20headphones%20around%20the%20neck%2C%20soft%20cream%20background%2C%20warm%20amber%20accent%20tones%2C%20minimal%20geometric%20shapes%2C%20centered%20square%20composition%2C%20clean%20no%20text&width=400&height=400&seq=gua-avatar-c4&orientation=squarish",
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20magazine%20illustration%20about%20a%20live%20shopping%20scandal%2C%20abstract%20stylized%20scene%20with%20empty%20shopping%20bags%20and%20a%20toppling%20microphone%20stand%2C%20warm%20cream%20and%20amber%20palette%2C%20moody%20dramatic%20lighting%2C%20no%20text%20or%20letters&width=800&height=600&seq=gua-cover-c4&orientation=landscape",
    summary:
      "以「只推自己用过的好东西」为口号的技术博主阿凯，在一场大促直播中被质疑推荐话术与实际体验差距过大，售后讨论迅速外溢到讨论区。",
    tags: ["只推自用", "口播夸张", "售后集中", "大促翻车"],
    focus: [
      "推荐词与产品实际体验存在明显落差",
      "同一产品在不同场次说法前后不一致",
      "售后问题在集中时段大量出现",
    ],
    timeline: [
      {
        date: "2025-10-15",
        title: "口碑积累期",
        description:
          "凭借拆解式测评积累起信任度，「只推自己用过的」成为账号标志性口号。",
      },
      {
        date: "2026-08-30",
        title: "大促专场",
        description:
          "一场持续六小时的专场直播上，多款产品被反复强调「闭眼入」，当晚销量创下新高。",
      },
      {
        date: "2026-09-01",
        title: "吐槽集中出现",
        description:
          "收货后大量用户发布体验反馈，指出与直播描述不符，相关话题迅速升温。",
      },
      {
        date: "2026-09-03",
        title: "旧场次被翻出",
        description:
          "网友找到半年前对同款产品的评价片段，与本次口播形成鲜明对比。",
      },
    ],
    quotes: [
      {
        date: "2025-10-15",
        text: "我推东西有个原则，自己没用过、没长期用，我不会开口。",
        source: "账号简介",
      },
      {
        date: "2026-08-30",
        text: "这个真的闭眼入，我自己家里一直在用，闭眼冲就完事了。",
        source: "直播回放片段",
      },
      {
        date: "2026-03-06",
        text: "这个价位能做到这样已经不错了，但确实别期待太高。",
        source: "往期测评视频",
      },
    ],
    outcome: "账号发布道歉短片，承认部分口播用词不严谨，宣布暂停带货一个月并启动售后登记。",
  },
  {
    id: "c5",
    slug: "yuyuanmama-yuer-zaojia",
    name: "芋圆妈妈说",
    handle: "@芋圆妈妈说",
    category: "虚假人设",
    platform: "图文社区",
    heat: 84,
    fallDate: "2026-04-11",
    avatar:
      "https://readdy.ai/api/search-image?query=Stylized%20flat%20editorial%20portrait%20illustration%20of%20a%20warm%20friendly%20young%20mother%20creator%20with%20a%20short%20bob%20haircut%2C%20soft%20cream%20background%2C%20muted%20sage%20green%20accent%20tones%2C%20minimal%20geometric%20shapes%2C%20centered%20square%20composition%2C%20clean%20no%20text&width=400&height=400&seq=gua-avatar-c5&orientation=squarish",
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20magazine%20illustration%20about%20a%20parenting%20persona%20exposed%2C%20abstract%20stylized%20scene%20with%20stacked%20storybooks%20and%20a%20faded%20rubber%20stamp%2C%20warm%20cream%20and%20sage%20green%20palette%2C%20moody%20dramatic%20lighting%2C%20no%20text%20or%20letters&width=800&height=600&seq=gua-cover-c5&orientation=landscape",
    summary:
      "分享育儿经验的芋圆妈妈说，长期以「从业经历丰富」为内容背书。网友比对其早年的职业描述后，发现经历存在拼接与夸大的痕迹。",
    tags: ["育儿人设", "经历拼接", "身份夸大", "家长质疑"],
    focus: [
      "职业经历描述存在拼接嫌疑",
      "同一段资历在不同场合年限不同",
      "经验分享的专业性受到质疑",
    ],
    timeline: [
      {
        date: "2024-06-08",
        title: "育儿内容起步",
        description:
          "从记录孩子日常开始，逐步转向成体系的育儿方法分享，粉丝以家长群体为主。",
      },
      {
        date: "2025-09-27",
        title: "引入专业背书",
        description:
          "在简介中加入过往职业描述，内容开始强调「经验总结」而非「个人体会」。",
      },
      {
        date: "2026-03-30",
        title: "读者提出疑问",
        description:
          "有家长整理其两年内对同一段经历的不同说法，指出年限与岗位存在出入。",
      },
      {
        date: "2026-04-11",
        title: "讨论扩散",
        description:
          "质疑帖获得大量转发，账号从「经验分享」被重新审视，评论区要求说明。",
      },
    ],
    quotes: [
      {
        date: "2025-09-27",
        text: "这些方法都是我在一线工作多年总结出来的，家长可以放心参考。",
        source: "主页简介",
      },
      {
        date: "2026-01-14",
        text: "其实我也就是个普通妈妈，很多都是自己摸索的，别太当真。",
        source: "直播互动片段",
      },
    ],
    outcome: "主页简介中职业描述被删除，改为「普通家长的实践笔记」，未再作额外说明。",
  },
  {
    id: "c6",
    slug: "chengnan-xiaozhu-shuju",
    name: "城南小筑",
    handle: "@城南小筑",
    category: "数据注水",
    platform: "图文社区",
    heat: 81,
    fallDate: "2026-03-19",
    avatar:
      "https://readdy.ai/api/search-image?query=Stylized%20flat%20editorial%20portrait%20illustration%20of%20an%20aesthetic%20lifestyle%20male%20creator%20with%20a%20linen%20shirt%2C%20soft%20cream%20background%2C%20warm%20brown%20accent%20tones%2C%20minimal%20geometric%20shapes%2C%20centered%20square%20composition%2C%20clean%20no%20text&width=400&height=400&seq=gua-avatar-c6&orientation=squarish",
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20magazine%20illustration%20about%20inflated%20engagement%20metrics%2C%20abstract%20stylized%20scene%20with%20a%20smooth%20climbing%20line%20chart%20and%20empty%20seats%2C%20warm%20cream%20and%20brown%20palette%2C%20moody%20dramatic%20lighting%2C%20no%20text%20or%20letters&width=800&height=600&seq=gua-cover-c6&orientation=landscape",
    summary:
      "以生活方式美学内容积累了大量粉丝的城南小筑，被第三方监测曲线「过于光滑」引发关注。互动与内容的匹配度被网友逐条核对。",
    tags: ["数据异常", "曲线光滑", "互动存疑", "美学账号"],
    focus: [
      "增长曲线在多个时段异常平滑",
      "高播放内容的评论质量与量不符",
      "不同平台粉丝画像差异明显",
    ],
    timeline: [
      {
        date: "2024-03-05",
        title: "美学内容稳定输出",
        description:
          "以居家长物与日常视角为主，形成了辨识度较高的视觉风格。",
      },
      {
        date: "2025-11-12",
        title: "增长曲线引注意",
        description:
          "有网友截取后台可见数据，指出多个时间段增长曲线过于平滑，缺少自然波动。",
      },
      {
        date: "2026-03-19",
        title: "讨论集中",
        description:
          "相关分析帖被大量转发，话题聚焦在内容质量与数据表现的匹配度上。",
      },
    ],
    quotes: [
      {
        date: "2026-03-19",
        text: "数据方面我们也不太懂，都是自然增长，没有买过任何东西。",
        source: "评论区回应",
      },
    ],
    outcome: "账号关闭了部分互动数据的公开展示，未进一步回应相关质疑。",
  },
  {
    id: "c7",
    slug: "lizijiang-fenjing-banyun",
    name: "栗子酱",
    handle: "@栗子酱酱",
    category: "抄袭搬运",
    platform: "视频平台",
    heat: 86,
    fallDate: "2026-06-14",
    avatar:
      "https://readdy.ai/api/search-image?query=Stylized%20flat%20editorial%20portrait%20illustration%20of%20a%20cheerful%20young%20female%20video%20creator%20with%20a%20ponytail%20and%20hoop%20earrings%2C%20soft%20cream%20background%2C%20warm%20coral%20accent%20tones%2C%20minimal%20geometric%20shapes%2C%20centered%20square%20composition%2C%20clean%20no%20text&width=400&height=400&seq=gua-avatar-c7&orientation=squarish",
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20magazine%20illustration%20about%20stolen%20video%20storyboards%2C%20abstract%20stylized%20scene%20with%20film%20frames%20and%20timeline%20strips%20misaligned%2C%20warm%20cream%20and%20coral%20palette%2C%20moody%20dramatic%20lighting%2C%20no%20text%20or%20letters&width=800&height=600&seq=gua-cover-c7&orientation=landscape",
    summary:
      "以强节奏剪辑见长的栗子酱，被指多条爆款视频的分镜结构与更早的作品高度重合。逐帧对比视频发布后引发围观。",
    tags: ["分镜重合", "逐帧对比", "爆款搬运", "剪辑争议"],
    focus: [
      "分镜切换节奏与更早作品高度接近",
      "转场设计与画面顺序几乎一致",
      "原作者在评论区现场对线",
    ],
    timeline: [
      {
        date: "2025-08-19",
        title: "强节奏风格成型",
        description:
          "凭借密集转场与强节奏剪辑，多条视频进入热门推荐。",
      },
      {
        date: "2026-05-30",
        title: "对比视频出现",
        description:
          "一位剪辑爱好者发布逐帧对比，指出分镜顺序与转场手法高度重合。",
      },
      {
        date: "2026-06-14",
        title: "原作者发声",
        description:
          "被对比作品的作者出现在评论区，双方就「借鉴」与「搬运」的边界展开争论。",
      },
    ],
    quotes: [
      {
        date: "2026-01-22",
        text: "剪辑这块我都是自己一点点磨出来的，节奏感是练出来的。",
        source: "置顶评论",
      },
      {
        date: "2026-06-15",
        text: "节奏这种东西本来就那几种，撞了很正常吧。",
        source: "评论区回应",
      },
    ],
    outcome: "被指重合度最高的两条视频转为仅自己可见，账号未发布正式说明。",
  },
  {
    id: "c8",
    slug: "laoli-ceping-shouqian",
    name: "老李评测",
    handle: "@老李评测",
    category: "带货翻车",
    platform: "视频平台",
    heat: 79,
    fallDate: "2026-02-25",
    avatar:
      "https://readdy.ai/api/search-image?query=Stylized%20flat%20editorial%20portrait%20illustration%20of%20a%20calm%20senior%20male%20tech%20reviewer%20with%20grey%20hair%20and%20glasses%2C%20soft%20cream%20background%2C%20muted%20olive%20accent%20tones%2C%20minimal%20geometric%20shapes%2C%20centered%20square%20composition%2C%20clean%20no%20text&width=400&height=400&seq=gua-avatar-c8&orientation=squarish",
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20magazine%20illustration%20about%20a%20biased%20product%20review%2C%20abstract%20stylized%20scene%20with%20a%20balance%20scale%20tipped%20to%20one%20side%20and%20coins%2C%20warm%20cream%20and%20olive%20palette%2C%20moody%20dramatic%20lighting%2C%20no%20text%20or%20letters&width=800&height=600&seq=gua-cover-c8&orientation=landscape",
    summary:
      "自称「只说真话」的老李评测，被指出多期内容与合作品牌高度重合。有网友整理出「好评清单」与商务合作的时间对应关系。",
    tags: ["测评立场", "合作重合", "只说真话", "清单对比"],
    focus: [
      "高评价产品的测评时间与商务合作重叠",
      "测评维度在合作期与非合作期不一致",
      "对同类产品的评价标准浮动明显",
    ],
    timeline: [
      {
        date: "2024-09-02",
        title: "中立形象建立",
        description:
          "以不带情绪的拆解式测评积累口碑，「只说真话」成为账号标签。",
      },
      {
        date: "2025-12-05",
        title: "评价标准被质疑",
        description:
          "有观众发现同类产品在不同期节目中的评分维度差异较大。",
      },
      {
        date: "2026-02-25",
        title: "清单对比流出",
        description:
          "网友整理出其高评价产品与合作品牌的时间对照表，讨论迅速升温。",
      },
    ],
    quotes: [
      {
        date: "2024-09-02",
        text: "我做测评只有一个标准，好不好用我自己先试，绝不含糊。",
        source: "账号简介",
      },
      {
        date: "2026-02-26",
        text: "合作和测评是分开的，我不会因为这个去改结论。",
        source: "评论区回应",
      },
    ],
    outcome: "账号在简介中补充了合作标识说明，部分旧视频加上了「商务合作」字样。",
  },
  {
    id: "c9",
    slug: "baozhuxiaozhang-yanxing",
    name: "暴躁小张",
    handle: "@暴躁小张",
    category: "言行打脸",
    platform: "短视频平台",
    heat: 88,
    fallDate: "2026-01-08",
    avatar:
      "https://readdy.ai/api/search-image?query=Stylized%20flat%20editorial%20portrait%20illustration%20of%20a%20bold%20young%20male%20creator%20with%20a%20buzz%20cut%20and%20thick%20eyebrows%2C%20soft%20cream%20background%2C%20warm%20orange%20accent%20tones%2C%20minimal%20geometric%20shapes%2C%20centered%20square%20composition%2C%20clean%20no%20text&width=400&height=400&seq=gua-avatar-c9&orientation=squarish",
    cover:
      "https://readdy.ai/api/search-image?query=Editorial%20magazine%20illustration%20about%20controversial%20online%20remarks%2C%20abstract%20stylized%20scene%20with%20a%20megaphone%20and%20scattered%20angry%20chat%20bubbles%2C%20warm%20cream%20and%20orange%20palette%2C%20moody%20dramatic%20lighting%2C%20no%20text%20or%20letters&width=800&height=600&seq=gua-cover-c9&orientation=landscape",
    summary:
      "以「敢说」为标签的暴躁小张，被翻出多段早期的激烈言论。这些言论与如今倡导的理性表达形成反差，成为讨论焦点。",
    tags: ["敢说人设", "早年言论", "反差明显", "立场反复"],
    focus: [
      "早年激烈言论与现立场相反",
      "「只讲道理」的口号与实际表达不符",
      "对不同对象使用双重标准",
    ],
    timeline: [
      {
        date: "2024-04-16",
        title: "靠敢说走红",
        description:
          "以直给、强观点的表达方式迅速积累关注，「敢说」成为核心标签。",
      },
      {
        date: "2025-07-09",
        title: "转型理性表达",
        description:
          "内容风格转向「讲道理、不动情绪」，并多次公开强调理性沟通。",
      },
      {
        date: "2025-12-28",
        title: "早年言论被整理",
        description:
          "网友整理出其两年前的激烈发言，与当前的理性倡导形成明显反差。",
      },
      {
        date: "2026-01-08",
        title: "讨论集中爆发",
        description:
          "整理帖被广泛转发，评论区围绕「改变」与「双标」展开激烈讨论。",
      },
    ],
    quotes: [
      {
        date: "2025-07-09",
        text: "我现在只讲逻辑，不带情绪，情绪解决不了任何问题。",
        source: "视频开场白",
      },
      {
        date: "2024-05-21",
        text: "（旧帖整理）讲什么道理，直接开怼就完了，讲道理的都是怂。",
        source: "早年发言截图",
      },
    ],
    outcome: "账号发布一条「我确实是变了」的说明短片，承认早期表达不当。",
  },
];

export const getCaseBySlug = (slug: string): GuaCase | undefined =>
  cases.find((item) => item.slug === slug);

export const getRelatedCases = (current: GuaCase, limit = 3): GuaCase[] => {
  const sameCategory = cases.filter(
    (item) => item.id !== current.id && item.category === current.category,
  );
  const others = cases.filter(
    (item) => item.id !== current.id && item.category !== current.category,
  );
  return [...sameCategory, ...others].slice(0, limit);
};